import Siema from 'siema';

import { satelliteTracker } from '../../services/tracking/satellite-tracking-service';
import { isWidthDeviceMobile } from '../../utils/device-utils';
import { truncate } from '../../utils/truncate';
import { hideElement, unHideElement } from '../../utils/dom-utils';
import { onLmpTabChange } from '../../utils/aem-utils';

const DATA_SLIDE_FIRST = 'first';
const DATA_SLIDE_MIDDLE = 'middle';
const DATA_SLIDE_LAST = 'last';
const SHOW_MORE_HTML = '... <span class="c-inline-gallery__show-more">altro</span>';
const ANIMATION = 150;

const track = {
  init: () => satelliteTracker.trackInWeb('gallery_inline_available'),
  prev: () => satelliteTracker.trackInWeb('gi_click_prev'),
  next: () => satelliteTracker.trackInWeb('gi_click_next'),
  slideView: (currentSlide) => {
    const slide = formatSlideNum(currentSlide);
    satelliteTracker.trackInWeb('change_gi', { slide });
  },
};

let $carousels = [];

const handleInitGallery = () => {
  const $inlineGalleries = document.querySelectorAll('.j-inline-gallery');
  if ($inlineGalleries.length) {
    track.init();
  }
  $inlineGalleries.forEach(($inlineGallery) => {
    const {
      updateCounterMsg,
      setInitialized,
      updateCounter,
      handleText,
      updateDataSlide,
      updateCopy,
      updateFooterCta,
      handlePrevClick,
      handleNextClick,
    } = getMethods($inlineGallery);

    updateCounterMsg();
    const $carousel = new Siema({
      selector: $inlineGallery.querySelector('.c-inline-gallery__carousel-wrapper'),
      onInit: () => {
        setInitialized();
        handleText(0);
      },
      onChange: () => {
        const { currentSlide } = $carousel;
        updateDataSlide(currentSlide);
        updateCounter(currentSlide);
        updateCopy(currentSlide);
        updateFooterCta(currentSlide);
        handleText(currentSlide);
        if (!isFirstSlide($inlineGallery) && !isLastSlide($inlineGallery)) {
          track.slideView(currentSlide);
        }
      },
    });
    $carousels.push($carousel);
    handlePrevClick($carousel);
    handleNextClick($carousel);
  });
};

const destroyCarousel = ($carousel) => {
  // Workaround to correctly destroy the carousel
  $carousel.goTo(0);
  $carousel.destroy(true);
};

export const initInlineGallery = () => {
  handleInitGallery();
  onLmpTabChange(() => {
    $carousels.forEach(($carousel) => {
      destroyCarousel($carousel);
    });
    $carousels = [];
    setTimeout(handleInitGallery, 500);
  });
};

function getElements($inlineGallery) {
  return {
    $prev: $inlineGallery.querySelector('.c-inline-gallery__prev'),
    $next: $inlineGallery.querySelector('.c-inline-gallery__next'),
    $counter: $inlineGallery.querySelector('.c-inline-gallery__counter .c-label'),
    $counterMsg: $inlineGallery.querySelector('.c-inline-gallery__counter-msg'),
    $copy: $inlineGallery.querySelector('.c-inline-gallery__copy'),
    $title: $inlineGallery.querySelector('.c-inline-gallery__title'),
    $footerCta: $inlineGallery.querySelector('.c-inline-gallery__footer'),
    $slides: $inlineGallery.querySelectorAll('.c-inline-gallery__carousel-wrapper img'),
    $textList: $inlineGallery.querySelectorAll('.c-inline-gallery__text'),
  };
}

function getMethods($inlineGallery) {
  const { $prev, $next, $counter, $counterMsg, $copy, $title, $footerCta, $slides, $textList } =
    getElements($inlineGallery);

  return {
    updateCounterMsg() {
      const remaining = Number($inlineGallery.dataset.count) - $slides.length + 1;
      $counterMsg.textContent = $counterMsg.textContent.replace('%s', remaining);
    },
    setInitialized() {
      $inlineGallery.dataset.initialized = true;
    },
    updateCounter(currentSlide) {
      $counter.textContent = `${currentSlide}/${$inlineGallery.dataset.count}`;
    },
    handleText(currentSlide) {
      $textList.forEach(hideElement);
      const $text = $textList[currentSlide];
      unHideElement($text);
      if (isWidthDeviceMobile() && !$text.dataset.initialized) {
        handleLineClamp({ $inlineGallery, $title, $text });
      }
    },
    updateDataSlide(currentSlide) {
      if (currentSlide === 0) {
        $inlineGallery.dataset.slide = DATA_SLIDE_FIRST;
        return;
      }
      if (currentSlide === $slides.length - 1) {
        $inlineGallery.dataset.slide = DATA_SLIDE_LAST;
        return;
      }
      $inlineGallery.dataset.slide = DATA_SLIDE_MIDDLE;
    },
    updateCopy(currentSlide) {
      const copy = $textList[currentSlide].dataset.copy;
      $copy.textContent = copy || '';
    },
    updateFooterCta(currentSlide) {
      if (!$footerCta.dataset.href) {
        $footerCta.dataset.href = $footerCta.href;
      }
      $footerCta.href = `${$footerCta.dataset.href}#${formatSlideNum(currentSlide + 1)}`;
    },
    handlePrevClick($carousel) {
      $prev.addEventListener('click', () => {
        $carousel.prev();
        track.prev();
      });
    },
    handleNextClick($carousel) {
      $next.addEventListener('click', () => {
        $carousel.next();
        track.next();
      });
    },
  };
}

function isFirstSlide($inlineGallery) {
  return $inlineGallery.dataset.slide === DATA_SLIDE_FIRST;
}

function isLastSlide($inlineGallery) {
  return $inlineGallery.dataset.slide === DATA_SLIDE_LAST;
}

function getTruncatedHTML($text) {
  if ($text.innerHTML.endsWith('</ul>') || $text.innerHTML.endsWith('</ol>')) {
    const $itemList = $text.querySelectorAll('li');
    const $lastItem = $itemList[$itemList.length - 1];
    if ($lastItem.querySelector('.c-inline-gallery__show-more')) {
      return $text.innerHTML;
    }
    $lastItem.innerHTML = SHOW_MORE_HTML;
    return $text.innerHTML;
  }

  if ($text.innerHTML.endsWith('<br></p>') || $text.innerHTML.endsWith('<br/></p>')) {
    const $brList = $text.querySelectorAll('br');
    const $lastBr = $brList[$brList.length - 1];
    $lastBr.remove();
    $text.querySelector('p').insertAdjacentHTML('beforeend', SHOW_MORE_HTML);
    return $text.innerHTML;
  }

  return $text.innerHTML;
}

function handleLineClamp({ $inlineGallery, $title, $text }) {
  $text.dataset.initialized = true;

  const initialHeight = `${$text.offsetHeight}px`;
  const originalHTML = $text.innerHTML;
  const { lines: textLines, lineHeight } = calcLines($text);
  let visibleTextLines = 4;

  if (isFirstSlide($inlineGallery)) {
    visibleTextLines = calcLines($title).lines === 1 ? 3 : 2;
  }

  if (textLines <= visibleTextLines) {
    return;
  }

  truncate($text, visibleTextLines, SHOW_MORE_HTML);

  const truncatedHTML = getTruncatedHTML($text);
  const truncatedHeight = `${lineHeight * visibleTextLines}px`;
  let open = false;

  $text.style.height = truncatedHeight;
  $text.addEventListener('click', () => {
    if (open) {
      setTimeout(() => ($text.innerHTML = truncatedHTML), ANIMATION);
      $text.style.height = truncatedHeight;
      open = false;
    } else {
      $text.innerHTML = originalHTML;
      $text.style.height = initialHeight;
      open = true;
    }
  });
}

function calcLines($element) {
  const style = window.getComputedStyle($element);
  const height = parseInt(style.height, 10);
  const lineHeight = parseInt(style.lineHeight, 10);
  return {
    lines: parseInt(height / lineHeight, 10),
    lineHeight,
  };
}

function formatSlideNum(currentSlide) {
  return currentSlide < 10 ? `0${currentSlide}` : `${currentSlide}`;
}
